import { useState } from "react";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IPersonalSiteFilter, ITableColumn, IWorkspaceFilter } from "@interfaces/common.interfaces";
import { IReportSectionProps } from "components/reports-common/report-section/report-section.props";
import ReportSection from "components/reports-common/report-section/report-section";
import {
    IWorkspaceSummaryInTenantReportDetails,
    IWorkspaceSummaryInTenantReportItem,
    IWorkspaceSummaryInTenantReportRequestModel
} from "@interfaces/reports/summary-reports.interfaces";
import WorkspaceSummaryInTenantReportSummary from "components/reports/summary-reports/workspace-summary-in-tenant-report/workspace-summary-in-tenant-report-summary";
import PersonalSiteFilter from "components/controls/personal-site-filter/personal-site-filter";
import WorkspaceLinkCell from "components/reports-common/report-results-grid-cells/workspace-link-cell/workspace-link-cell";
import DateTimeCell from "components/reports-common/report-results-grid-cells/date-time-cell/date-time-cell";
import SizeCell from "components/reports-common/report-results-grid-cells/size-cell/size-cell";
import StoragePercentageCell from "components/reports-common/report-results-grid-cells/storage-percentage-cell/storage-percentage-cell";
import CheckedCell from "components/reports-common/report-results-grid-cells/checked-cell/checked-cell";
import PrincipalsCell from "components/reports-common/report-results-grid-cells/principals-cell/principals-cell";
import { getWorkspaceTypeKey } from "lib/workspaces";
import { escapeForExport } from "lib/utils";
import { isWorkspaceFilterValid } from "lib/request-model-validation-helper";
import WorkspaceFilter from "components/controls/workspace-filter/workspace-filter";

const reportUrl: string = "/api/reports/workspace-summary-in-tenant";

export default function WorkspaceSummaryInTenantReport() {
    const { t } = useL10n<ILocaleStrings>();

    const [model, setModel] = useState<IWorkspaceSummaryInTenantReportRequestModel>({
        personalSiteFilter: {
            includeWorkspaces: true,
            includeOneDrive: false
        }
    });
    const [personalSiteFilter, setPersonalSiteFilter] = useState<IPersonalSiteFilter>({
        includeWorkspaces: true,
        includeOneDrive: false
    });
    const [workspaceFilter, setWorkspaceFilter] = useState<IWorkspaceFilter>({
        applyFilter: false
    });

    // Define table columns
    const cols: Array<ITableColumn<IWorkspaceSummaryInTenantReportItem>> = [
        // Workspace (link column)
        {
            key: "workspaceName",
            label: t("ppaWorkspace"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 200,
            renderCell: (key: string, item: IWorkspaceSummaryInTenantReportItem) => (
                <WorkspaceLinkCell
                    name={item.workspaceName}
                    url={item.workspaceUrl}
                    template={item.workspaceTemplate}
                    type={item.workspaceType}
                />
            )
        },
        // Workspace URL (export only)
        {
            key: "workspaceUrl",
            label: t("ppaWorkspaceUrl"),
            showInGrid: false,
            showInExport: true
        },
        // Workspace Type
        {
            key: "workspaceType",
            label: t("ppaWorkspaceType"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 130,
            renderCell: (key: string, item: IWorkspaceSummaryInTenantReportItem) => (
                t(getWorkspaceTypeKey(item.workspaceType))
            ),
            getExportValue: (key: string, item: IWorkspaceSummaryInTenantReportItem) => (
                t(getWorkspaceTypeKey(item.workspaceType))
            )
        },
        // Workspace Template (export only)
        {
            key: "workspaceTemplate",
            label: t("ppaWorkspaceTemplate"),
            showInGrid: false,
            showInExport: true
        },
        // Inactive (checkbox)
        {
            key: "isInactive",
            label: t("ppaInactive"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 90,
            renderCell: (key: string, item: IWorkspaceSummaryInTenantReportItem) => (
                <CheckedCell columnId={key} item={item} checkedValue={true} />
            ),
            getExportValue: (key: string, item: IWorkspaceSummaryInTenantReportItem) => (
                item.isInactive ? t("ppaYes") : t("ppaNo")
            )
        },
        // Orphaned (checkbox)
        {
            key: "isOrphaned",
            label: t("ppaOrphaned"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 100,
            renderCell: (key: string, item: IWorkspaceSummaryInTenantReportItem) => (
                <CheckedCell columnId={key} item={item} checkedValue={true} />
            ),
            getExportValue: (key: string, item: IWorkspaceSummaryInTenantReportItem) => (
                item.isOrphaned ? t("ppaYes") : t("ppaNo")
            )
        },
        // Owners
        {
            key: "owners",
            label: t("ppaOwners"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 200,
            renderCell: (key: string, item: IWorkspaceSummaryInTenantReportItem) => (
                <PrincipalsCell columnId={key} item={item} />
            ),
            getExportValue: (key: string, item: IWorkspaceSummaryInTenantReportItem) => {
                return escapeForExport((item as any)[key]);
            }
        },
        // Owner UPNs (export only)
        {
            key: "ownerUpns",
            label: t("ppaOwnerUpns"),
            showInGrid: false,
            showInExport: true,
            getExportValue: (key: string, item: IWorkspaceSummaryInTenantReportItem) => {
                return escapeForExport(item.owners?.map(o => o.upn) || []);
            }
        },
        // Last Modified
        {
            key: "lastModified",
            label: t("lastModified"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 150,
            renderCell: (key: string, item: IWorkspaceSummaryInTenantReportItem) => (
                <DateTimeCell columnId={key} item={item} pattern="PPp" />
            )
        },
        // Total Size
        {
            key: "totalSize",
            label: t("ppaTotalSize"),
            showInGrid: true,
            showInExport: false,
            defaultWidth: 120,
            renderCell: (key: string, item: IWorkspaceSummaryInTenantReportItem) => (
                <SizeCell value={item.totalSize} valueUnits="B" />
            )
        },
        // Total Size Bytes (export only)
        {
            key: "totalSizeBytes",
            label: t("ppaTotalSizeBytes"),
            showInGrid: false,
            showInExport: true,
            getExportValue: (key: string, item: IWorkspaceSummaryInTenantReportItem) => item.totalSize.toString()
        },
        // % of Quota
        {
            key: "percentageOfQuota",
            label: t("ppaPercentageOfQuota"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 180,
            renderCell: (key: string, item: IWorkspaceSummaryInTenantReportItem, details?: IWorkspaceSummaryInTenantReportDetails) => (
                <StoragePercentageCell
                    percentageUsed={item.percentageOfQuota}
                    usedValue={item.totalSize}
                    usedValueUnits="B"
                    maxValue={details?.storageQuotaMB || 0}
                    maxValueUnits="MB"
                />
            )
        },
        // User Visits (export only)
        {
            key: "userVisits",
            label: t("ppaUserVisits"),
            showInGrid: false,
            showInExport: true
        },
        // Active Users (export only)
        {
            key: "activeUsers",
            label: t("ppaActiveUsers"),
            showInGrid: false,
            showInExport: true
        }
    ];

    const onPersonalSiteFilterChange = (value?: IPersonalSiteFilter) => {
        setPersonalSiteFilter({
            includeWorkspaces: value?.includeWorkspaces ?? false,
            includeOneDrive: value?.includeOneDrive ?? false
        });
        setModel({
            ...model,
            personalSiteFilter: {
                includeWorkspaces: value?.includeWorkspaces,
                includeOneDrive: value?.includeOneDrive
            }
        });
    };

    const onWorkspaceFilterChange = (value?: IWorkspaceFilter) => {
        setWorkspaceFilter({
            applyFilter: value?.applyFilter || false,
            filter: value?.filter
        });
        setModel({
            ...model,
            workspaceFilter: value?.applyFilter ?
                {
                    property: value?.filter?.property,
                    operator: value?.filter?.operator,
                    value: value?.filter?.value
                }
                : undefined
        });
    };

    // Model is valid if at least one toggle is true
    const isModelValid = (): boolean => {
        const hasPersonalSiteFilter = (model.personalSiteFilter?.includeWorkspaces || model.personalSiteFilter?.includeOneDrive) === true;

        return hasPersonalSiteFilter && isWorkspaceFilterValid(workspaceFilter.applyFilter, model.workspaceFilter);
    };

    const reportSectionProps: IReportSectionProps<IWorkspaceSummaryInTenantReportItem, IWorkspaceSummaryInTenantReportDetails> = {
        columns: cols,
        summaryComponent: <WorkspaceSummaryInTenantReportSummary />,
        reportUrl: reportUrl,
        model: model,
        isModelValid: isModelValid(),
        noItemsMessage: t("ppaWorkspaceSummaryInTenantReportNoItemsMessage"),
        defaultExportFilenamePrefix: "ppaWorkspaceSummaryInTenantReportDefaultExportFilenamePrefix"
    };

    return (
        <ReportSection {...reportSectionProps}>
            <WorkspaceFilter model={workspaceFilter} onChange={onWorkspaceFilterChange} />
            <PersonalSiteFilter model={personalSiteFilter} onChange={onPersonalSiteFilterChange} />
        </ReportSection>
    );
}
